<template>
  <b-card>
    <b-row>
      <b-col>
        <b-row align-h="between">
          <b-col cols="auto">
            <b-row>
              <b-col cols="auto">
                <b-avatar variant="primary" size="40" class="shadow mb-2">
                  <i class="fas fa-coins text-white cursor-pointer icon-2x" />
                </b-avatar>
              </b-col>
              <b-col align-self="center" cols="auto">
                <h1>Bonus Details</h1>
              </b-col>
            </b-row>
          </b-col>
          <b-col cols="auto">
            <b-dropdown right text="Filters" variant="primary" boundary="window">
              <b-dropdown-form>
                <b-form-checkbox-group v-model="selectedFilters">
                  <b-form-checkbox value="value_achieved">Value Achieved</b-form-checkbox>
                  <b-form-checkbox value="base_bonus">Base Bonus</b-form-checkbox>
                  <b-form-checkbox value="extra_bonus">Extra Bonus</b-form-checkbox>
                  <b-form-checkbox value="appointments_attended"
                    >Appointments Attended</b-form-checkbox
                  >
                  <b-form-checkbox value="total">Total</b-form-checkbox>
                </b-form-checkbox-group>
              </b-dropdown-form>
            </b-dropdown>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <b-row>
      <!-- Estado de carga -->
      <div v-if="loading" class="text-center w-100">
        <b-spinner variant="primary"></b-spinner>
        <p>Loading bonus data...</p>
      </div>

      <!-- Contenido principal -->
      <template v-else>
        <b-col cols="9">
          <vue-apex-charts
            v-if="series.length > 0"
            :options="chartOptions"
            :series="series"
            height="450"
            type="bar" />
        </b-col>
        <b-col cols="3">
          <b-row v-for="({ label, value }, key) in avgs" :key="key">
            <vue-apex-charts
              :options="radialBarOptions(label, value)"
              :series="[value]"
              type="radialBar" />
          </b-row>
        </b-col>
      </template>
    </b-row>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BAvatar,
  BDropdown,
  BDropdownForm,
  BFormCheckboxGroup,
  BFormCheckbox,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import axiosB from '@/core/services/api/admin/bonus'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BAvatar,
    BDropdown,
    BDropdownForm,
    BFormCheckboxGroup,
    BFormCheckbox,
    VueApexCharts,
  },
  props: {
    date: {
      type: Object,
      required: true,
      validator: value =>
        typeof value.start !== 'undefined' && typeof value.end !== 'undefined',
    },
    // Nueva prop para recibir datos del dashboard
    dashboardBonus: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      bonusData: [],
      selectedFilters: [
        'value_achieved',
        'base_bonus',
        'extra_bonus',
        'appointments_attended',
        'total',
      ],
      chartOptions: {
        chart: { id: 'bonus-bar-chart', type: 'bar', toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        xaxis: { categories: [] },
        yaxis: { title: { text: 'Value' } },
        stroke: { show: true, width: 1 },
      },
      series: [],
      avgs: [],
      userData: null,
    }
  },
  watch: {
    date: {
      async handler(newDate) {
        if (newDate.start && newDate.end) {
          await this.loadBonusData()
        }
      },
      deep: true,
      immediate: true,
    },
    dashboardBonus: {
      async handler(newBonus) {
        if (newBonus && Object.keys(newBonus).length > 0) {
          await this.processDashboardBonus(newBonus)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async loadBonusData() {
      try {
        this.loading = true
        this.userData = JSON.parse(localStorage.getItem('userData'))

        // Si tenemos datos del dashboard, usarlos
        if (this.dashboardBonus && Object.keys(this.dashboardBonus).length > 0) {
          await this.processDashboardBonus(this.dashboardBonus)
        } else {
          // Si no, hacer llamada a la API
          const response = await axiosB.bonusByUser({
            user_id: this.userData.id,
            rol_id: this.userData.roles[0].id,
            start_date: this.date.start,
            end_date: this.date.end,
          })

          this.bonusData = response.data.bonus
          this.prepareChartData()
          this.prepareRadialBarData()
        }
      } catch (error) {
        console.error('Error fetching bonus details:', error)
        this.bonusData = []
      } finally {
        this.loading = false
      }
    },

    async processDashboardBonus(dashboardBonus) {
      try {
        this.loading = true
        
        // Convertir datos del dashboard al formato esperado por el componente
        const processedData = this.convertDashboardBonusToUserFormat(dashboardBonus)
        
        this.bonusData = processedData
        this.prepareChartData()
        this.prepareRadialBarData()
      } catch (error) {
        console.error('Error processing dashboard bonus data:', error)
        this.bonusData = []
      } finally {
        this.loading = false
      }
    },

    convertDashboardBonusToUserFormat(dashboardBonus) {
      const userData = JSON.parse(localStorage.getItem('userData'))
      const userId = userData.id
      const processedData = []

      // Iterar sobre cada ubicación
      Object.values(dashboardBonus).forEach(location => {
        // Iterar sobre cada fecha en la ubicación
        Object.entries(location).forEach(([date, dateData]) => {
          // Filtrar citas del usuario actual
          const userAppointments = dateData.appointments.filter(appt => 
            appt.user && appt.user.id === userId
          )

          if (userAppointments.length > 0) {
            // Calcular totales para el usuario en esta fecha
            const userTotals = userAppointments.reduce((acc, appt) => ({
              base_bonus: acc.base_bonus + (parseFloat(appt.base_bonus) || 0),
              extra_bonus: acc.extra_bonus + (parseFloat(appt.extra_bonus) || 0),
              total: acc.total + (parseFloat(appt.total) || 0),
              value_achieved: acc.value_achieved + (parseFloat(appt.value_achieved) || 0),
              appointments_attended: acc.appointments_attended + 1
            }), {
              base_bonus: 0,
              extra_bonus: 0,
              total: 0,
              value_achieved: 0,
              appointments_attended: 0
            })

            // Agregar entrada procesada
            processedData.push({
              day: date,
              base_bonus: userTotals.base_bonus,
              extra_bonus: userTotals.extra_bonus,
              total: userTotals.total,
              value_achieved: userTotals.value_achieved,
              appointments_attended: userTotals.appointments_attended
            })
          }
        })
      })

      // Ordenar por fecha
      return processedData.sort((a, b) => new Date(a.day) - new Date(b.day))
    },

    prepareChartData() {
      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: this.bonusData.map(bonus => {
            const date = new Date(bonus.day)
            return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
          }),
        },
      }

      this.series = [
        {
          name: 'Base Bonus',
          data: this.bonusData.map(bonus => this.parseNumber(bonus.base_bonus)),
        },
        {
          name: 'Extra Bonus',
          data: this.bonusData.map(bonus => this.parseNumber(bonus.extra_bonus)),
        },
        {
          name: 'Total',
          data: this.bonusData.map(bonus => this.parseNumber(bonus.total)),
        },
      ]
    },
    prepareRadialBarData() {
      if (!this.bonusData?.length) return

      // Calcular sumatorias de todos los días
      const totals = this.bonusData.reduce(
        (acc, bonus) => ({
          base_bonus: acc.base_bonus + this.parseNumber(bonus.base_bonus),
          extra_bonus: acc.extra_bonus + this.parseNumber(bonus.extra_bonus),
          total: acc.total + this.parseNumber(bonus.total),
        }),
        { base_bonus: 0, extra_bonus: 0, total: 0 }
      )

      this.avgs = [
        { label: 'Total Base Bonus', value: (totals.base_bonus / 500) * 100 },
        { label: 'Total Extra Bonus', value: (totals.extra_bonus / 500) * 100},
        { label: 'Grand Total', value: (totals.total / 500) * 100},
      ]
    },
    parseNumber(value) {
      const number = parseFloat(value)
      return isNaN(number) ? 0 : Number(number.toFixed(2))
    },
    radialBarOptions(label, value) {
      return {
        chart: { type: 'radialBar' },
        plotOptions: {
          radialBar: {
            hollow: { size: '50%' },
            dataLabels: {
              name: { show: true, fontSize: '9px' },
              value: {
                show: true,
                fontSize: '14px',
                formatter: () => `${this.parseNumber((value) * 500 / 100)}`,
              },
            },
          },
        },
        labels: [label],
      };
    },
    percentage(value) {
      return this.parseNumber(value)
    },
  },
}
</script>

<style scoped>
.text-success {
  color: #28a745 !important;
}
.text-primary {
  color: #007bff !important;
}
.text-warning {
  color: #ffc107 !important;
}
.text-danger {
  color: #dc3545 !important;
}

.loading-wrapper {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
